/**
 * Tenant public marketing config (browser entry point).
 *
 * The pure parse/serialize/resolve logic + constants live in
 * `publicMarketingConfigCore.ts` (no imports, safe for Node serverless). This
 * module re-exports that core and layers on the browser-only pieces: app-mode
 * aware defaults (`isSaasPlatform`) and Supabase-backed fetchers.
 */
import { isSaasPlatform } from './companyBranding.js';
import { supabase } from '../lib/supabase.js';
import { resolveDocumentOrganizationId } from './documentBrandContext.js';
import {
  getCodeDefaultPublicMarketingConfig as getCodeDefaultCore,
  resolvePublicMarketingConfig as resolveCore,
  parsePublicMarketingConfig,
  PUBLIC_MARKETING_PLATFORM_CONFIG_KEY,
  type PublicMarketingConfig,
  type PublicMarketingConfigPartial,
} from './publicMarketingConfigCore.js';

export * from './publicMarketingConfigCore.js';

/** Code-level default config, app-mode aware (SaaS platform vs Tools & Bubbles). */
export function getCodeDefaultPublicMarketingConfig(): PublicMarketingConfig {
  return getCodeDefaultCore(isSaasPlatform());
}

/** Merge org + platform partials over the app-mode aware code defaults. */
export function resolvePublicMarketingConfig(
  orgPartial?: PublicMarketingConfigPartial | null,
  platformPartial?: PublicMarketingConfigPartial | null,
): PublicMarketingConfig {
  return resolveCore(orgPartial, platformPartial, isSaasPlatform());
}

const PLATFORM_PUBLIC_MARKETING_KEY = PUBLIC_MARKETING_PLATFORM_CONFIG_KEY;

export async function fetchPlatformPublicMarketingDefault(): Promise<PublicMarketingConfigPartial> {
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', PLATFORM_PUBLIC_MARKETING_KEY)
      .maybeSingle();
    if (error || !data?.value) return {};
    return parsePublicMarketingConfig(data.value);
  } catch {
    return {};
  }
}

export async function fetchPublicMarketingConfigForOrg(
  organizationId?: string | null,
): Promise<PublicMarketingConfig> {
  const [platformPartial, resolvedOrgId] = await Promise.all([
    fetchPlatformPublicMarketingDefault(),
    resolveDocumentOrganizationId(organizationId),
  ]);

  if (!resolvedOrgId) {
    return resolvePublicMarketingConfig(null, platformPartial);
  }

  try {
    const { data } = await supabase
      .from('company_settings')
      .select('public_marketing_config')
      .eq('organization_id', resolvedOrgId)
      .maybeSingle();
    return resolvePublicMarketingConfig(
      parsePublicMarketingConfig(data?.public_marketing_config),
      platformPartial,
    );
  } catch {
    return resolvePublicMarketingConfig(null, platformPartial);
  }
}
